// Meta Pixel + Conversions API browser-side helper
// Fires browser pixel events and sends matching server-side events for deduplication

/**
 * Generate a unique event ID shared between browser + server for deduplication
 */
export function generateEventId() {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/** Get _fbp cookie (Facebook browser ID) */
export function getFbp() {
  const m = document.cookie.match(/_fbp=([^;]+)/);
  return m ? m[1] : null;
}

/** Get _fbc cookie (Facebook click ID), or build from fbclid URL param */
export function getFbc() {
  const m = document.cookie.match(/_fbc=([^;]+)/);
  if (m) return m[1];
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null;
}

/**
 * Fire a browser-side fbq event with optional eventID for deduplication
 */
function fbqTrack(eventName, params = {}, eventId) {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', eventName, params, eventId ? { eventID: eventId } : {});
}

/**
 * Send a matching server-side event via /api/meta-conversion
 */
async function sendServerEvent(eventName, { eventId, userData = {}, customData = {} } = {}) {
  try {
    await fetch('/api/meta-conversion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        user_data: {
          ...userData,
          client_user_agent: navigator.userAgent,
          fbp: getFbp(),
          fbc: getFbc(),
        },
        custom_data: customData,
      }),
    });
  } catch (err) {
    console.error('[Meta CAPI]', err.message);
  }
}

// ── Public API ──────────────────────────────────────────────

/** PageView — fires on every page load / SPA navigation */
export function trackPageView() {
  const eventId = generateEventId();
  fbqTrack('PageView', {}, eventId);
  sendServerEvent('PageView', { eventId });
}

/** ViewContent — fires on service / product / song detail pages */
export function trackViewContent({ contentName, contentCategory, value, currency = 'EUR' } = {}) {
  const eventId = generateEventId();
  const params = {};
  if (contentName) params.content_name = contentName;
  if (contentCategory) params.content_category = contentCategory;
  if (value != null) { params.value = value; params.currency = currency; }
  fbqTrack('ViewContent', params, eventId);
  sendServerEvent('ViewContent', { eventId, customData: params });
}

/** InitiateCheckout — fires when user starts the payment flow */
export function trackInitiateCheckout({ value, currency = 'EUR', contentName, email } = {}) {
  const eventId = generateEventId();
  const params = {};
  if (value != null) { params.value = value; params.currency = currency; }
  if (contentName) params.content_name = contentName;
  fbqTrack('InitiateCheckout', params, eventId);
  sendServerEvent('InitiateCheckout', {
    eventId,
    userData: email ? { email } : {},
    customData: params,
  });
  // GA4 begin_checkout event
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'begin_checkout', {
      value: value,
      currency: currency,
    });
  }
}

/** Purchase — fires only after confirmed payment */
export function trackPurchase({ value, currency = 'EUR', email, transactionId } = {}) {
  const eventId = generateEventId();
  const params = { value, currency };
  if (transactionId) params.order_id = transactionId;
  fbqTrack('Purchase', params, eventId);
  sendServerEvent('Purchase', {
    eventId,
    userData: email ? { email } : {},
    customData: params,
  });
  // GA4 purchase event
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
    });
  }
}

/** Contact — fires when user submits the song request form */
export function trackContact({ email, contentName } = {}) {
  const eventId = generateEventId();
  const params = {};
  if (contentName) params.content_name = contentName;
  fbqTrack('Contact', params, eventId);
  sendServerEvent('Contact', {
    eventId,
    userData: email ? { email } : {},
    customData: params,
  });
}
